import React, { useMemo, useEffect, useState, useContext } from "react";
import "../css/RedditPost.css";
import TagsList from "./TagsList";
import { Link } from "react-router-dom";
import Carousel from "react-material-ui-carousel";
import { Box, Card, CardMedia, Typography } from "@mui/material";
import Utils from "../utils/Utils";
import { useNavigate } from "react-router-dom";
import useVote from "../hooks/useVote";
import { DeviceContext } from "../context/DeviceContext";
import { useTheme } from "./ThemeContext";
import PostActions from "./PostActions";
import { getImageUrl } from "../utils/imageUrl";

// ─────────────────────────────────────────────────────────────────────────────
// Unified post card — renders as list row (default) or tile card (viewMode="tile")
// ─────────────────────────────────────────────────────────────────────────────
const PostMain = ({ post, updatePosts, viewMode = "list" }) => {
  const navigate = useNavigate();
  const { isMobile } = useContext(DeviceContext);
  const { theme } = useTheme();
  const { handleVote, upvotes, downvotes } = useVote(post, updatePosts);

  const formattedTime = Utils.getFormattedTime(post.createdAt);
  const postContent = post.content || "";
  const previewContent = postContent.replace(/\s*\n+\s*/g, " ").trim();

  const memoizedImages = useMemo(
    () => (Array.isArray(post.images) ? post.images : []),
    [post.images],
  );

  // List-mode overflow detection — hooks must always run regardless of viewMode
  const [isContentOverflowing, setIsContentOverflowing] = useState(false);
  const [contentNode, setContentNode] = useState(null);

  const memoizedCarousel = useMemo(() => {
    if (memoizedImages.length > 0) {
      return (
        <Carousel
          key={post.postId}
          swipe={true}
          height={isMobile ? "13rem" : "15rem"}
          autoPlay={false}>
          {memoizedImages.map((image) => {
            const imageUrl = getImageUrl(image.url);
            return (
              <Card
                key={image.imageId}
                sx={{ backgroundColor: "#111f34", borderRadius: 0 }}>
                <CardMedia
                  component="img"
                  sx={{
                    objectFit: "cover",
                    height: isMobile ? "13rem" : "15rem",
                  }}
                  image={imageUrl}
                  alt={`image-${image.imageId}`}
                />
              </Card>
            );
          })}
        </Carousel>
      );
    }
    return null;
  }, [memoizedImages, post.postId, isMobile]);

  useEffect(() => {
    if (!contentNode) return;
    const checkOverflow = () => {
      setIsContentOverflowing(
        contentNode.scrollHeight > contentNode.clientHeight + 1,
      );
    };
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [contentNode, previewContent, isMobile]);

  const postUrl = `/community/${post.communityName}/${post.postId}`;

  // ── TILE MODE ─────────────────────────────────────────────────────────────
  if (viewMode === "tile") {
    const firstImage = memoizedImages[0] || null;
    const firstImageUrl = firstImage ? getImageUrl(firstImage.url) : null;

    return (
      <Box
        onClick={() => navigate(postUrl)}
        sx={{
          backgroundColor: "#000000",
          borderRadius: "14px",
          border: "1px solid #1e2939",
          overflow: "hidden",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          transition:
            "border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease",
          "&:hover": {
            borderColor: "rgba(0,201,80,0.35)",
            transform: "translateY(-2px)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
            // title turns green on card hover
            "& .tile-card-title": { color: "#05df72" },
            // image zooms on card hover
            "& .tile-card-img": { transform: "scale(1.05)" },
          },
        }}>
        {/* Image — only rendered when post actually has one */}
        {firstImageUrl && (
          <Box
            sx={{
              height: 160,
              overflow: "hidden",
              flexShrink: 0,
            }}>
            <CardMedia
              component="img"
              image={firstImageUrl}
              alt={post.title}
              className="tile-card-img"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: "transform 0.35s ease",
              }}
            />
          </Box>
        )}

        {/* Content */}
        <Box
          sx={{ p: 1.75, display: "flex", flexDirection: "column", flex: 1 }}>
          {/* Tags */}
          {post.tags?.length > 0 && (
            <Box sx={{ mb: 1 }} onClick={(e) => e.stopPropagation()}>
              <TagsList tags={post.tags} large />
            </Box>
          )}

          {/* Title — 2-line clamp */}
          <Typography
            component={Link}
            to={postUrl}
            onClick={(e) => e.stopPropagation()}
            className="tile-card-title"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 700,
              fontSize: { xs: "1rem", md: "1rem" },
              lineHeight: 1.35,
              letterSpacing: "-0.02em",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textDecoration: "none",
              mb: 0.75,
              transition: "color 0.15s ease",
            }}>
            {post.title}
          </Typography>

          {/* Meta */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.4,
              flexWrap: "wrap",
              mb: 1,
            }}>
            <Typography
              sx={{
                color: theme.palette.primary.light,
                fontSize: { xs: "0.875rem", md: "0.75rem" },
                fontWeight: 600,
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/community/${post.communityName}`);
              }}>
              c/{post.communityName}
            </Typography>
            <Typography
              sx={{
                color: theme.palette.text.muted,
                fontSize: { xs: "0.75rem", md: "0.75rem" },
              }}>
              •
            </Typography>
            <Typography
              sx={{
                color: theme.palette.text.muted,
                fontSize: { xs: "0.75rem", md: "0.75rem" },
              }}>
              {formattedTime}
            </Typography>
            <Typography
              sx={{
                color: theme.palette.text.muted,
                fontSize: { xs: "0.75rem", md: "0.75rem" },
              }}>
              by
            </Typography>
            <Typography
              sx={{
                color: "#05df72",
                fontSize: { xs: "0.75rem", md: "0.75rem" },
                fontWeight: 600,
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/profile/${post.userName}`);
              }}>
              {post.userName}
            </Typography>
          </Box>

          {/* Preview text — 2-line clamp */}
          {previewContent && (
            <Typography
              sx={{
                fontSize: { xs: "0.875rem", md: "0.875rem" },
                color: theme.palette.text.primary,
                lineHeight: "1.25rem",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}>
              {previewContent}
            </Typography>
          )}
        </Box>

        {/* Actions */}
        <Box onClick={(e) => e.stopPropagation()}>
          <PostActions
            post={post}
            handleVote={handleVote}
            upvotes={upvotes}
            downvotes={downvotes}
            sizeMultiplier={0.9}
            paddingMultiplier={0.55}
            compactBorder
            sx={{
              backgroundColor: "transparent",
              borderBottomLeftRadius: "14px",
              borderBottomRightRadius: "14px",
            }}
          />
        </Box>
      </Box>
    );
  }

  // ── LIST MODE ─────────────────────────────────────────────────────────────
  return (
    <div className="reddit-post">
      {post.images && post.images.length > 0 && memoizedCarousel}
      <div className="post-tags-wrap">
        <TagsList tags={post.tags} />
      </div>

      <div className="post-info">
        <Link to={postUrl} style={{ textDecoration: "none" }}>
          <h2 className="post-feed-title" style={{ textAlign: "left" }}>
            {post.title}
          </h2>
        </Link>

        <Box
          className="post-meta-row"
          sx={{
            gap: "0.3rem",
            flexDirection: "row",
            alignItems: "center",
            flexWrap: "wrap",
            marginTop: ".6rem",
            marginBottom: ".6rem",
          }}>
          <span
            className="community"
            style={{ margin: 0 }}
            onClick={() => navigate(`/community/${post.communityName}`)}>
            @{post.communityName}
          </span>
          <span className="post-meta-dot">•</span>
          <span className="time-user" style={{ margin: 0 }}>
            {formattedTime} by
          </span>
          <span
            className="username"
            style={{ margin: 0 }}
            onClick={() => navigate(`/profile/${post.userName}`)}>
            {post.userName}
          </span>
        </Box>

        <Box className="content post-feed-body" sx={{ position: "relative" }}>
          <div
            ref={setContentNode}
            style={{
              whiteSpace: "normal",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              fontSize: "0.875rem",
              color: theme.palette.text.primary,
              lineHeight: "1.25rem",
            }}>
            {previewContent}
          </div>
          {isContentOverflowing && (
            <Box
              component={Link}
              to={postUrl}
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                pl: "3rem",
                background:
                  "linear-gradient(to right, rgba(0,0,0,0), rgba(0,0,0,1) 50%)",
                color: "#05df72",
                fontSize: "0.875rem",
                fontWeight: 500,
                textDecoration: "none",
                lineHeight: "1.25rem",
                pr: "0.25rem",
              }}>
              Read more
            </Box>
          )}
        </Box>
      </div>

      <PostActions
        post={post}
        handleVote={handleVote}
        upvotes={upvotes}
        downvotes={downvotes}
        sizeMultiplier={1.15}
        paddingMultiplier={0.5}
        sx={{ borderTop: "none" }}
      />
    </div>
  );
};

export default PostMain;
